import { useCallback } from "react"

import { useMutation, useQuery } from "@tanstack/react-query"
import { AxiosError } from "axios"

import { axiosClient } from "$/http"

import { hours, seconds } from "@/3514/utils/time.utils"
import Urls from "@/api/urls"
import { CoachingGoalStatus } from "@/constants"

import { CoachingGoalApi, CoachingGoalModel } from "../model/types/coaching-goal"

export enum QueryKey {
    CoachingGoal = "coachingGoal"
}

export const getCoachingGoal = async ({ queryKey }): Promise<CoachingGoalApi[]> => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_key, { id, query }] = queryKey
    const { data } = await axiosClient.get(Urls.coachingGoal(id, query))
    return data
}

export function useCoachingGoalData(participantId: number, query: CoachingGoalStatus) {
    return useQuery<CoachingGoalApi[], AxiosError, CoachingGoalModel[]>({
        queryKey: [QueryKey.CoachingGoal, { query, id: participantId }],
        queryFn: getCoachingGoal,
        select: useCallback(data => data.map(goal => new CoachingGoalModel(goal)), []),
        staleTime: hours(2),
        retry: 1,
        retryDelay: seconds(5),
        throwOnError: false
    })
}

export function useArchivedCoachingGoalData(participantId: number) {
    return useQuery<CoachingGoalApi[], AxiosError, number[]>({
        queryKey: [QueryKey.CoachingGoal, { query: "archived", id: participantId }],
        queryFn: getCoachingGoal,
        select: useCallback(data => data.map(goal => goal.id), [])
    })
}

export function useCreateCoachingGoal(participantId: number) {
    return useMutation({
        mutationFn: (coachingGoal: Pick<CoachingGoalApi, "description" | "order">) =>
            axiosClient.post<{ id: number }>(Urls.createCoachingGoal(participantId), coachingGoal)
    })
}

export function useEditCoachingGoal(participantId: number) {
    return useMutation({
        mutationFn: (coachingGoal: Pick<CoachingGoalApi, "description" | "order" | "id">) =>
            axiosClient.put(Urls.editCoachingGoal(participantId, coachingGoal.id), {
                description: coachingGoal.description,
                order: coachingGoal.order
            })
    })
}

export function useArchiveCoachingGoal(participantId: number) {
    return useMutation({
        mutationFn: (goalId: number) => axiosClient.post(Urls.archiveCoachingGoal(participantId, goalId))
    })
}

export function useUnarchiveCoachingGoal(participantId: number) {
    return useMutation({
        mutationFn: (goalId: number) => axiosClient.post(Urls.unarchiveCoachingGoal(participantId, goalId))
    })
}

export function useSortCoachingGoals(participantId: number) {
    return useMutation({
        mutationFn: (sort: { ids: number[] }) => axiosClient.post(Urls.sortCoachingGoals(participantId), sort)
    })
}
